import React, { useState, useCallback } from 'react';
import { useTheme } from '../context/ThemeContext';
import { PageHeader, Card, btnPrimary, btnSm } from '../components/UI';
import { RankBars, DashCard } from '../components/DashboardWidgets';

/* ─────────────────────────────────────────────────────────────────────────────
   Grand Memories Resort & Spa — SEO Intelligence Module
   Uses Anthropic API (claude-sonnet-4-20250514) with web_search tool to do
   live keyword research, competitor analysis, and meta-tag generation.
───────────────────────────────────────────────────────────────────────────── */

const AI_MODEL = 'claude-sonnet-4-20250514';

async function callClaude(systemPrompt, userPrompt, useSearch = true) {
  const body = {
    model: AI_MODEL,
    max_tokens: 2000,
    system: systemPrompt,
    messages: [{ role: 'user', content: userPrompt }],
  };
  if (useSearch) {
    body.tools = [{ type: 'web_search_20250305', name: 'web_search' }];
  }
  const res = await fetch('https://api.anthropic.com/v1/messages', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json();
  if (data.error) throw new Error(data.error.message);
  // Collect all text blocks (tool_use blocks are searched internally)
  return data.content
    .filter(b => b.type === 'text')
    .map(b => b.text)
    .join('\n');
}

function parseJSON(text) {
  try {
    const m = text.match(/```json\s*([\s\S]+?)\s*```/) || text.match(/(\{[\s\S]+\}|\[[\s\S]+\])/);
    return JSON.parse(m ? m[1] : text.trim());
  } catch { return null; }
}

const ACCENT = '#c45e3e';
const GREEN  = '#2ecc71';
const BLUE   = '#3b82f6';
const PURPLE = '#8b5cf6';
const GOLD   = '#c8a96e';

/* ─── Copy-to-clipboard helper ─────────────────────────────────────────────── */
function CopyBtn({ text }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  };
  return (
    <button onClick={copy} style={{ ...btnSm, fontSize:10, padding:'3px 8px', color: copied ? GREEN : 'var(--textMuted)', borderColor: copied ? GREEN : 'var(--border)', transition:'all .2s' }}>
      {copied ? '✓ Copied' : 'Copy'}
    </button>
  );
}

/* ─── Keyword pill ──────────────────────────────────────────────────────────── */
function KwPill({ kw, volume, difficulty, intent }) {
  const diffColor = difficulty === 'low' ? GREEN : difficulty === 'medium' ? GOLD : '#e74c3c';
  const intentMap = { informational: BLUE, transactional: GREEN, navigational: PURPLE, commercial: GOLD };
  return (
    <div style={{ background:'var(--bgInput)', border:'1px solid var(--border)', borderRadius:8, padding:'10px 12px', display:'flex', flexDirection:'column', gap:5, position:'relative', overflow:'hidden' }}>
      <div style={{ position:'absolute', left:0, top:0, bottom:0, width:3, borderRadius:'8px 0 0 8px', background:intentMap[intent] || ACCENT }}/>
      <div style={{ paddingLeft:6 }}>
        <div style={{ fontSize:12, fontWeight:700, color:'var(--textPrimary)', marginBottom:3 }}>{kw}</div>
        <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
          {volume && <span style={{ fontSize:9, color:'var(--textFaint)', fontWeight:600 }}>VOL: {volume}</span>}
          {difficulty && <span style={{ fontSize:9, fontWeight:700, color:diffColor, background:diffColor+'18', padding:'1px 5px', borderRadius:10, textTransform:'uppercase' }}>{difficulty}</span>}
          {intent && <span style={{ fontSize:9, color:intentMap[intent]||ACCENT, background:(intentMap[intent]||ACCENT)+'18', padding:'1px 5px', borderRadius:10 }}>{intent}</span>}
        </div>
      </div>
    </div>
  );
}

/* ─── Section loader bar ──────────────────────────────────────────────────────── */
function LoadingBar({ label }) {
  return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:12, padding:'32px 0' }}>
      <div style={{ width:240, height:3, background:'var(--border)', borderRadius:3, overflow:'hidden' }}>
        <div style={{ height:'100%', width:'60%', background:`linear-gradient(90deg,${ACCENT},${GOLD},${ACCENT})`, borderRadius:3, animation:'seoSlide 1.4s ease-in-out infinite', backgroundSize:'200% 100%' }}/>
      </div>
      <div style={{ fontSize:12, color:'var(--textFaint)', letterSpacing:0.5 }}>{label}</div>
      <style>{`@keyframes seoSlide { 0%{background-position:200% 0} 100%{background-position:-200% 0} }`}</style>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   MAIN SEO PAGE COMPONENT
══════════════════════════════════════════════════════════════════════════════ */
export default function SeoPage({ notify }) {
  const { theme } = useTheme();
  const hotelName    = theme.hotelName    || 'Grand Memories Resort & Spa';
  const hotelAddress = theme.hotelAddress || 'Nakuru, Kenya';
  const tagline      = theme.hotelTagline || 'Lodge & Retreat';

  // Tabs
  const [tab, setTab] = useState('research');

  // ── Keyword Research state ──────────────────────────────────────────────
  const [resLoading, setResLoading] = useState(false);
  const [resResult,  setResResult]  = useState(null);
  const [resError,   setResError]   = useState('');
  const [resCustom,  setResCustom]  = useState('');

  // ── Meta Tags state ─────────────────────────────────────────────────────
  const [metaLoading, setMetaLoading] = useState(false);
  const [metaResult,  setMetaResult]  = useState(null);
  const [metaError,   setMetaError]   = useState('');
  const [metaPage,    setMetaPage]    = useState('homepage');

  // ── Competitor Analysis state ───────────────────────────────────────────
  const [compLoading, setCompLoading] = useState(false);
  const [compResult,  setCompResult]  = useState(null);
  const [compError,   setCompError]   = useState('');

  // ── Content Ideas state ─────────────────────────────────────────────────
  const [contentLoading, setContentLoading] = useState(false);
  const [contentResult,  setContentResult]  = useState(null);
  const [contentError,   setContentError]   = useState('');

  /* ── Run Keyword Research ─────────────────────────────────────────────── */
  const runResearch = useCallback(async () => {
    setResLoading(true);
    setResError('');
    setResResult(null);
    try {
      const sys = `You are an expert hotel SEO strategist. You MUST use the web_search tool multiple times to research real, current data before answering. Search for:
1. Top keywords competitors rank for
2. Current trending hotel search terms in the region
3. Long-tail keywords with high conversion potential
4. Local SEO keywords for the specific location

After searching, return ONLY a JSON object with NO markdown prose outside the JSON block. Format:
{
  "primary": [{"kw":"keyword","volume":"est. monthly searches","difficulty":"low|medium|high","intent":"informational|transactional|navigational|commercial","why":"brief reason"}],
  "longtail": [{"kw":"...","volume":"...","difficulty":"...","intent":"...","why":"..."}],
  "local": [{"kw":"...","volume":"...","difficulty":"...","intent":"...","why":"..."}],
  "seasonal": [{"kw":"...","season":"...","note":"..."}],
  "summary": "2-3 sentence strategic overview",
  "topOpportunity": "The single best keyword opportunity right now"
}`;
      const extra = resCustom.trim() ? ` Focus especially on: ${resCustom}` : '';
      const usr = `Research the best SEO keywords for ${hotelName}, a ${tagline} hotel located in ${hotelAddress}.${extra}
Search the web to find: what keywords rival hotels in ${hotelAddress} rank for, what travellers search for when looking for hotels in this area, and trending hospitality keywords in Kenya/East Africa right now. Provide at least 6 keywords per category.`;
      const text = await callClaude(sys, usr, true);
      const json = parseJSON(text);
      if (!json) throw new Error('Could not parse AI response. Raw: ' + text.slice(0, 200));
      setResResult(json);
    } catch (e) {
      setResError(e.message);
    } finally {
      setResLoading(false);
    }
  }, [hotelName, hotelAddress, tagline, resCustom]);

  /* ── Generate Meta Tags ───────────────────────────────────────────────── */
  const runMeta = useCallback(async () => {
    setMetaLoading(true);
    setMetaError('');
    setMetaResult(null);
    try {
      const sys = `You are an expert hotel SEO copywriter. Use web_search to check what meta tags top-ranking hotels in Kenya use, then create optimised meta tags. Return ONLY a JSON object:
{
  "pages": [
    {
      "page": "page name",
      "title": "SEO title (50-60 chars)",
      "description": "meta description (150-160 chars)",
      "keywords": ["kw1","kw2","kw3","kw4","kw5"],
      "og_title": "Open Graph title",
      "og_description": "OG description for social sharing",
      "schema_type": "Hotel|LodgingBusiness|Restaurant etc",
      "h1": "Suggested H1 heading"
    }
  ],
  "localBusinessSchema": { complete JSON-LD schema object for the hotel },
  "tips": ["tip1","tip2","tip3"]
}`;
      const pages = {
        homepage: 'homepage, rooms overview',
        rooms: 'rooms & accommodation page',
        restaurant: 'restaurant & dining page',
        pool: 'pool & amenities page',
        conference: 'conference & events page',
        contact: 'contact & location page',
      };
      const usr = `Generate complete SEO meta tags for the ${metaPage === 'all' ? 'all main pages' : pages[metaPage] || metaPage} of ${hotelName} (${tagline}), located in ${hotelAddress}.
Search the web to check current best practices for hotel meta tags and what top Kenya hotels use. Make tags compelling, locally optimised, and keyword-rich.`;
      const text = await callClaude(sys, usr, true);
      const json = parseJSON(text);
      if (!json) throw new Error('Could not parse AI response.');
      setMetaResult(json);
    } catch (e) {
      setMetaError(e.message);
    } finally {
      setMetaLoading(false);
    }
  }, [hotelName, hotelAddress, tagline, metaPage]);

  /* ── Competitor Analysis ──────────────────────────────────────────────── */
  const runComp = useCallback(async () => {
    setCompLoading(true);
    setCompError('');
    setCompResult(null);
    try {
      const sys = `You are an SEO competitive analyst for the hospitality industry. Use web_search EXTENSIVELY to find real competitor hotels in the area and analyse their SEO. Return ONLY JSON:
{
  "competitors": [
    {
      "name": "Hotel Name",
      "website": "domain.com",
      "strengths": ["str1","str2"],
      "keywords": ["kw1","kw2","kw3"],
      "estimatedRank": "position 1-5 on Google for main terms",
      "gap": "keyword or strategy gap our hotel can exploit"
    }
  ],
  "opportunities": [
    {"keyword":"...","currentLeader":"...","difficulty":"low|medium|high","strategy":"how to outrank"}
  ],
  "ourAdvantages": ["advantage1","advantage2","advantage3"],
  "quickWins": ["actionable quick win 1","actionable quick win 2","actionable quick win 3"],
  "summary": "2-3 sentence competitive landscape overview"
}`;
      const usr = `Search the web to find the top 4-5 hotel competitors of ${hotelName} in ${hotelAddress} and nearby areas. Look at their websites, Google listings, and what keywords they rank for. Identify SEO gaps and opportunities for ${hotelName} to dominate local search results.`;
      const text = await callClaude(sys, usr, true);
      const json = parseJSON(text);
      if (!json) throw new Error('Could not parse AI response.');
      setCompResult(json);
    } catch (e) {
      setCompError(e.message);
    } finally {
      setCompLoading(false);
    }
  }, [hotelName, hotelAddress]);

  /* ── Content Ideas ────────────────────────────────────────────────────── */
  const runContent = useCallback(async () => {
    setContentLoading(true);
    setContentError('');
    setContentResult(null);
    try {
      const sys = `You are a hotel content marketing strategist. Use web_search to find trending hotel blog topics, what travel bloggers write about Kenya/East Africa, and what guests search for. Return ONLY JSON:
{
  "blogPosts": [
    {"title":"...","targetKeyword":"...","intent":"...","outline":["h2 section 1","h2 section 2","h2 section 3"],"estimatedTraffic":"low|medium|high","priority":"1-5"}
  ],
  "socialContent": [
    {"platform":"Instagram|Facebook|TikTok","idea":"...","hashtags":["#tag1","#tag2"],"bestTime":"..."}
  ],
  "localContent": [
    {"idea":"...","keyword":"...","why":"..."}
  ],
  "contentCalendar": [
    {"month":"...","theme":"...","keyContent":"...","seoFocus":"..."}
  ]
}`;
      const usr = `Generate a complete SEO content strategy for ${hotelName} in ${hotelAddress}. Search the web to find what travel content about Nakuru/Kenya gets the most engagement, what questions travellers ask about visiting this area, and what content gaps exist that ${hotelName} could fill to rank higher on Google.`;
      const text = await callClaude(sys, usr, true);
      const json = parseJSON(text);
      if (!json) throw new Error('Could not parse AI response.');
      setContentResult(json);
    } catch (e) {
      setContentError(e.message);
    } finally {
      setContentLoading(false);
    }
  }, [hotelName, hotelAddress]);

  const TABS = [
    { id:'research',    label:'🔍 Keyword Research',      desc:'Find the best keywords to rank for' },
    { id:'meta',        label:'🏷️ Meta Tags',             desc:'Generate optimised page titles & descriptions' },
    { id:'competitors', label:'🏆 Competitor Analysis',   desc:'See what rivals rank for and beat them' },
    { id:'content',     label:'✍️ Content Strategy',      desc:'Blog & social ideas that drive traffic' },
  ];

  return (
    <div>
      <PageHeader icon="🎯" title="SEO Intelligence" sub="AI-powered keyword research & search optimisation — powered by live web analysis" />

      {/* Hotel context strip */}
      <div style={{ background:'var(--bgInput)', border:'1px solid var(--border)', borderRadius:10, padding:'10px 16px', marginBottom:18, display:'flex', gap:16, alignItems:'center', flexWrap:'wrap' }}>
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          <div style={{ width:6, height:6, borderRadius:'50%', background:GREEN }}/>
          <span style={{ fontSize:11, color:'var(--textFaint)' }}>Analysing:</span>
          <span style={{ fontSize:12, fontWeight:700, color:'var(--textPrimary)' }}>{hotelName}</span>
        </div>
        <div style={{ width:1, height:16, background:'var(--border)' }}/>
        <span style={{ fontSize:11, color:'var(--textFaint)' }}>📍 {hotelAddress}</span>
        <div style={{ width:1, height:16, background:'var(--border)' }}/>
        <span style={{ fontSize:11, color:'var(--textFaint)' }}>🏷️ {tagline}</span>
        <div style={{ marginLeft:'auto', fontSize:10, color:ACCENT, fontWeight:600 }}>
          ⚡ Uses live web search — results reflect current market conditions
        </div>
      </div>

      {/* Tab bar */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8, marginBottom:20 }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            background: tab===t.id ? `${ACCENT}10` : 'var(--bgCard)',
            border: `1px solid ${tab===t.id ? ACCENT+'50' : 'var(--border)'}`,
            borderRadius:10, padding:'12px 14px', cursor:'pointer', fontFamily:'inherit',
            textAlign:'left', transition:'all .15s',
          }}>
            <div style={{ fontSize:12, fontWeight:700, color: tab===t.id ? ACCENT : 'var(--textPrimary)', marginBottom:3 }}>{t.label}</div>
            <div style={{ fontSize:10, color:'var(--textFaint)', lineHeight:1.4 }}>{t.desc}</div>
          </button>
        ))}
      </div>

      {/* ══ KEYWORD RESEARCH ══════════════════════════════════════════════════ */}
      {tab === 'research' && (
        <div style={{ display:'grid', gap:16 }}>
          <DashCard title="Research Settings">
            <div style={{ display:'flex', gap:10, alignItems:'flex-end', flexWrap:'wrap' }}>
              <div style={{ flex:1, minWidth:200 }}>
                <label style={{ fontSize:10, color:'var(--textFaint)', fontWeight:600, textTransform:'uppercase', letterSpacing:1.2, display:'block', marginBottom:5 }}>
                  Focus Area (optional)
                </label>
                <input
                  value={resCustom}
                  onChange={e => setResCustom(e.target.value)}
                  placeholder="e.g. conference facilities, honeymoon packages, safari tours..."
                  style={{ width:'100%', background:'var(--bgInput)', border:'1px solid var(--borderMid)', borderRadius:7, padding:'9px 12px', color:'var(--textPrimary)', fontSize:13, fontFamily:'inherit', boxSizing:'border-box', outline:'none' }}
                />
              </div>
              <button onClick={runResearch} disabled={resLoading} style={{ ...btnPrimary, width:'auto', padding:'10px 28px', opacity: resLoading ? 0.6 : 1 }}>
                {resLoading ? '⏳ Searching web…' : '🔍 Find Best Keywords'}
              </button>
            </div>
            <div style={{ marginTop:10, fontSize:10, color:'var(--textFaint)', lineHeight:1.6 }}>
              The AI will search the web in real time — checking competitor rankings, Google Trends, and hospitality forums — to find keywords with the best traffic potential for your hotel.
            </div>
          </DashCard>

          {resLoading && <LoadingBar label="Searching the web for keywords, competitor rankings, and search trends…" />}
          {resError && <div style={{ background:'var(--bgDanger)', border:'1px solid #e74c3c44', borderRadius:8, padding:'12px 16px', color:'#e74c3c', fontSize:12 }}>⚠ {resError}</div>}

          {resResult && (
            <div style={{ display:'grid', gap:14 }}>
              {/* Top opportunity banner */}
              {resResult.topOpportunity && (
                <div style={{ background:`linear-gradient(135deg,${ACCENT}15,${GOLD}10)`, border:`1px solid ${ACCENT}30`, borderRadius:10, padding:'14px 18px', display:'flex', alignItems:'center', gap:12 }}>
                  <div style={{ fontSize:24 }}>🏆</div>
                  <div>
                    <div style={{ fontSize:10, color:ACCENT, fontWeight:800, textTransform:'uppercase', letterSpacing:1.5, marginBottom:3 }}>Top Keyword Opportunity Right Now</div>
                    <div style={{ fontSize:15, fontWeight:800, color:'var(--textPrimary)' }}>{resResult.topOpportunity}</div>
                  </div>
                </div>
              )}

              {/* Summary */}
              {resResult.summary && (
                <DashCard>
                  <div style={{ fontSize:13, color:'var(--textSecond)', lineHeight:1.7 }}>📊 {resResult.summary}</div>
                </DashCard>
              )}

              {/* Primary keywords */}
              {resResult.primary?.length > 0 && (
                <DashCard title="Primary Keywords" titleColor={ACCENT} action={<CopyBtn text={resResult.primary.map(k=>k.kw).join(', ')} />}>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))', gap:8 }}>
                    {resResult.primary.map((k, i) => <KwPill key={i} {...k} />)}
                  </div>
                </DashCard>
              )}

              {/* Long-tail */}
              {resResult.longtail?.length > 0 && (
                <DashCard title="Long-Tail Keywords" titleColor={BLUE} action={<CopyBtn text={resResult.longtail.map(k=>k.kw).join('\n')} />}>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))', gap:8 }}>
                    {resResult.longtail.map((k, i) => <KwPill key={i} {...k} />)}
                  </div>
                  <div style={{ marginTop:10, padding:'8px 12px', background:'var(--bgInput)', borderRadius:7, fontSize:10, color:'var(--textFaint)' }}>
                    💡 Long-tail keywords are easier to rank for and have higher purchase intent — perfect for driving direct bookings.
                  </div>
                </DashCard>
              )}

              {/* Local SEO */}
              {resResult.local?.length > 0 && (
                <DashCard title="Local SEO Keywords" titleColor={GREEN} action={<CopyBtn text={resResult.local.map(k=>k.kw).join(', ')} />}>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))', gap:8 }}>
                    {resResult.local.map((k, i) => <KwPill key={i} {...k} />)}
                  </div>
                </DashCard>
              )}

              {/* Seasonal */}
              {resResult.seasonal?.length > 0 && (
                <DashCard title="Seasonal & Event Keywords" titleColor={GOLD}>
                  <div style={{ display:'grid', gap:8 }}>
                    {resResult.seasonal.map((k, i) => (
                      <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 12px', background:'var(--bgInput)', borderRadius:7 }}>
                        <div>
                          <div style={{ fontSize:12, fontWeight:700, color:'var(--textPrimary)' }}>{k.kw}</div>
                          {k.note && <div style={{ fontSize:10, color:'var(--textFaint)', marginTop:2 }}>{k.note}</div>}
                        </div>
                        <span style={{ fontSize:10, color:GOLD, fontWeight:700, background:GOLD+'18', padding:'2px 8px', borderRadius:10 }}>{k.season}</span>
                      </div>
                    ))}
                  </div>
                </DashCard>
              )}
            </div>
          )}
        </div>
      )}

      {/* ══ META TAGS ══════════════════════════════════════════════════════════ */}
      {tab === 'meta' && (
        <div style={{ display:'grid', gap:16 }}>
          <DashCard title="Meta Tag Generator">
            <div style={{ display:'flex', gap:10, alignItems:'flex-end', flexWrap:'wrap' }}>
              <div>
                <label style={{ fontSize:10, color:'var(--textFaint)', fontWeight:600, textTransform:'uppercase', letterSpacing:1.2, display:'block', marginBottom:5 }}>Page</label>
                <select value={metaPage} onChange={e => setMetaPage(e.target.value)} style={{ background:'var(--bgInput)', border:'1px solid var(--borderMid)', borderRadius:7, padding:'9px 12px', color:'var(--textPrimary)', fontSize:13, fontFamily:'inherit', cursor:'pointer', outline:'none' }}>
                  {[['homepage','Homepage'],['rooms','Rooms & Accommodation'],['restaurant','Restaurant & Dining'],['pool','Pool & Amenities'],['conference','Conference & Events'],['contact','Contact & Location'],['all','All Pages']].map(([v,l]) => (
                    <option key={v} value={v}>{l}</option>
                  ))}
                </select>
              </div>
              <button onClick={runMeta} disabled={metaLoading} style={{ ...btnPrimary, width:'auto', padding:'10px 28px', opacity:metaLoading?0.6:1 }}>
                {metaLoading ? '⏳ Generating…' : '🏷️ Generate Meta Tags'}
              </button>
            </div>
          </DashCard>

          {metaLoading && <LoadingBar label="Researching top hotel meta tags and generating optimised copy…" />}
          {metaError && <div style={{ background:'var(--bgDanger)', border:'1px solid #e74c3c44', borderRadius:8, padding:'12px 16px', color:'#e74c3c', fontSize:12 }}>⚠ {metaError}</div>}

          {metaResult && (
            <div style={{ display:'grid', gap:12 }}>
              {metaResult.pages?.map((pg, i) => (
                <DashCard key={i} title={`📄 ${pg.page}`} titleColor={BLUE}
                  action={<CopyBtn text={`<title>${pg.title}</title>\n<meta name="description" content="${pg.description}">\n<meta name="keywords" content="${(pg.keywords||[]).join(', ')}">`} />}>
                  <div style={{ display:'grid', gap:10 }}>
                    {/* Title */}
                    <div>
                      <div style={{ fontSize:9, color:'var(--textFaint)', fontWeight:700, textTransform:'uppercase', letterSpacing:1.2, marginBottom:4 }}>
                        Title Tag <span style={{ color: pg.title?.length > 60 ? '#e74c3c' : GREEN }}>({pg.title?.length} chars)</span>
                      </div>
                      <div style={{ background:'var(--bgInput)', borderRadius:7, padding:'9px 12px', fontSize:13, color:'var(--textPrimary)', fontWeight:600, fontFamily:'monospace' }}>{pg.title}</div>
                    </div>
                    {/* Description */}
                    <div>
                      <div style={{ fontSize:9, color:'var(--textFaint)', fontWeight:700, textTransform:'uppercase', letterSpacing:1.2, marginBottom:4 }}>
                        Meta Description <span style={{ color: pg.description?.length > 160 ? '#e74c3c' : GREEN }}>({pg.description?.length} chars)</span>
                      </div>
                      <div style={{ background:'var(--bgInput)', borderRadius:7, padding:'9px 12px', fontSize:12, color:'var(--textSecond)', lineHeight:1.6, fontFamily:'monospace' }}>{pg.description}</div>
                    </div>
                    {/* Keywords + H1 + OG */}
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                      <div>
                        <div style={{ fontSize:9, color:'var(--textFaint)', fontWeight:700, textTransform:'uppercase', letterSpacing:1.2, marginBottom:4 }}>Keywords</div>
                        <div style={{ display:'flex', flexWrap:'wrap', gap:5 }}>
                          {(pg.keywords||[]).map((k,j) => <span key={j} style={{ fontSize:10, color:ACCENT, background:ACCENT+'12', border:`1px solid ${ACCENT}25`, borderRadius:20, padding:'2px 8px', fontWeight:600 }}>{k}</span>)}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize:9, color:'var(--textFaint)', fontWeight:700, textTransform:'uppercase', letterSpacing:1.2, marginBottom:4 }}>Suggested H1</div>
                        <div style={{ fontSize:12, color:'var(--textSecond)', fontStyle:'italic' }}>{pg.h1}</div>
                      </div>
                    </div>
                    {pg.og_title && (
                      <div>
                        <div style={{ fontSize:9, color:'var(--textFaint)', fontWeight:700, textTransform:'uppercase', letterSpacing:1.2, marginBottom:4 }}>Open Graph (Social)</div>
                        <div style={{ background:'var(--bgInput)', borderRadius:7, padding:'9px 12px' }}>
                          <div style={{ fontSize:12, fontWeight:700, color:'var(--textPrimary)' }}>{pg.og_title}</div>
                          <div style={{ fontSize:11, color:'var(--textMuted)', marginTop:2 }}>{pg.og_description}</div>
                        </div>
                      </div>
                    )}
                  </div>
                </DashCard>
              ))}

              {/* Schema */}
              {metaResult.localBusinessSchema && (
                <DashCard title="📋 JSON-LD Schema (Paste in <head>)" titleColor={PURPLE}
                  action={<CopyBtn text={'<script type="application/ld+json">\n' + JSON.stringify(metaResult.localBusinessSchema, null, 2) + '\n</script>'} />}>
                  <pre style={{ background:'var(--bgInput)', borderRadius:7, padding:'12px', fontSize:10, color:'var(--textSecond)', overflowX:'auto', lineHeight:1.6, margin:0, fontFamily:'monospace' }}>
                    {JSON.stringify(metaResult.localBusinessSchema, null, 2)}
                  </pre>
                </DashCard>
              )}

              {/* Tips */}
              {metaResult.tips?.length > 0 && (
                <DashCard title="💡 SEO Tips">
                  <div style={{ display:'grid', gap:6 }}>
                    {metaResult.tips.map((tip, i) => (
                      <div key={i} style={{ display:'flex', gap:10, padding:'8px 12px', background:'var(--bgInput)', borderRadius:7 }}>
                        <span style={{ color:GOLD, fontWeight:800, flexShrink:0 }}>{i+1}.</span>
                        <span style={{ fontSize:12, color:'var(--textSecond)', lineHeight:1.5 }}>{tip}</span>
                      </div>
                    ))}
                  </div>
                </DashCard>
              )}
            </div>
          )}
        </div>
      )}

      {/* ══ COMPETITOR ANALYSIS ════════════════════════════════════════════════ */}
      {tab === 'competitors' && (
        <div style={{ display:'grid', gap:16 }}>
          <DashCard title="Competitor Intelligence">
            <div style={{ fontSize:12, color:'var(--textMuted)', marginBottom:12, lineHeight:1.6 }}>
              The AI will search Google to identify your real competitors in {hotelAddress}, analyse their SEO strategies, and surface the exact gaps you can exploit to outrank them.
            </div>
            <button onClick={runComp} disabled={compLoading} style={{ ...btnPrimary, width:'auto', padding:'10px 28px', opacity:compLoading?0.6:1 }}>
              {compLoading ? '⏳ Scanning competitors…' : '🏆 Analyse Competitors'}
            </button>
          </DashCard>

          {compLoading && <LoadingBar label="Searching Google for competitors, scanning their rankings and SEO strategies…" />}
          {compError && <div style={{ background:'var(--bgDanger)', border:'1px solid #e74c3c44', borderRadius:8, padding:'12px 16px', color:'#e74c3c', fontSize:12 }}>⚠ {compError}</div>}

          {compResult && (
            <div style={{ display:'grid', gap:14 }}>
              {/* Summary */}
              {compResult.summary && (
                <div style={{ background:`${BLUE}10`, border:`1px solid ${BLUE}30`, borderRadius:10, padding:'14px 18px', fontSize:13, color:'var(--textSecond)', lineHeight:1.7 }}>
                  📊 {compResult.summary}
                </div>
              )}

              {/* Our advantages */}
              {compResult.ourAdvantages?.length > 0 && (
                <DashCard title={`✅ ${hotelName}'s SEO Advantages`} titleColor={GREEN}>
                  <div style={{ display:'grid', gap:6 }}>
                    {compResult.ourAdvantages.map((a, i) => (
                      <div key={i} style={{ display:'flex', gap:10, padding:'8px 12px', background:GREEN+'0a', borderRadius:7, border:`1px solid ${GREEN}20` }}>
                        <span style={{ color:GREEN, fontWeight:800 }}>✓</span>
                        <span style={{ fontSize:12, color:'var(--textSecond)' }}>{a}</span>
                      </div>
                    ))}
                  </div>
                </DashCard>
              )}

              {/* Competitors grid */}
              {compResult.competitors?.length > 0 && (
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:12 }}>
                  {compResult.competitors.map((comp, i) => (
                    <DashCard key={i} title={`🏨 ${comp.name}`} titleColor={PURPLE}>
                      {comp.website && <div style={{ fontSize:10, color:BLUE, marginBottom:8 }}>🔗 {comp.website}</div>}
                      {comp.estimatedRank && <div style={{ fontSize:10, color:GOLD, marginBottom:8, fontWeight:600 }}>📍 Ranking: {comp.estimatedRank}</div>}
                      {comp.keywords?.length > 0 && (
                        <div style={{ marginBottom:8 }}>
                          <div style={{ fontSize:9, color:'var(--textFaint)', fontWeight:700, textTransform:'uppercase', letterSpacing:1, marginBottom:5 }}>Ranking Keywords</div>
                          <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
                            {comp.keywords.map((k, j) => <span key={j} style={{ fontSize:10, background:'var(--bgInput)', color:'var(--textSecond)', padding:'2px 7px', borderRadius:10, border:'1px solid var(--border)' }}>{k}</span>)}
                          </div>
                        </div>
                      )}
                      {comp.gap && (
                        <div style={{ background:ACCENT+'12', border:`1px solid ${ACCENT}25`, borderRadius:7, padding:'8px 10px', fontSize:11, color:ACCENT, fontWeight:600 }}>
                          💡 Gap: {comp.gap}
                        </div>
                      )}
                    </DashCard>
                  ))}
                </div>
              )}

              {/* Opportunities */}
              {compResult.opportunities?.length > 0 && (
                <DashCard title="🎯 Keyword Gaps & Opportunities" titleColor={ACCENT}>
                  <div style={{ display:'grid', gap:8 }}>
                    {compResult.opportunities.map((op, i) => (
                      <div key={i} style={{ padding:'12px 14px', background:'var(--bgInput)', borderRadius:8, display:'grid', gap:4 }}>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                          <span style={{ fontSize:13, fontWeight:700, color:'var(--textPrimary)' }}>{op.keyword}</span>
                          <span style={{ fontSize:9, color: op.difficulty==='low'?GREEN:op.difficulty==='medium'?GOLD:'#e74c3c', fontWeight:700, background:(op.difficulty==='low'?GREEN:op.difficulty==='medium'?GOLD:'#e74c3c')+'18', padding:'2px 7px', borderRadius:10, textTransform:'uppercase' }}>{op.difficulty}</span>
                        </div>
                        {op.currentLeader && <div style={{ fontSize:10, color:'var(--textFaint)' }}>Current leader: {op.currentLeader}</div>}
                        {op.strategy && <div style={{ fontSize:11, color:BLUE, marginTop:2 }}>Strategy: {op.strategy}</div>}
                      </div>
                    ))}
                  </div>
                </DashCard>
              )}

              {/* Quick wins */}
              {compResult.quickWins?.length > 0 && (
                <DashCard title="⚡ Quick Wins — Do These First" titleColor={GOLD}>
                  <div style={{ display:'grid', gap:6 }}>
                    {compResult.quickWins.map((win, i) => (
                      <div key={i} style={{ display:'flex', gap:12, padding:'10px 12px', background:GOLD+'0a', borderRadius:7, border:`1px solid ${GOLD}20` }}>
                        <span style={{ color:GOLD, fontWeight:800, flexShrink:0, fontSize:14 }}>{i+1}</span>
                        <span style={{ fontSize:12, color:'var(--textSecond)', lineHeight:1.5 }}>{win}</span>
                      </div>
                    ))}
                  </div>
                </DashCard>
              )}
            </div>
          )}
        </div>
      )}

      {/* ══ CONTENT STRATEGY ══════════════════════════════════════════════════ */}
      {tab === 'content' && (
        <div style={{ display:'grid', gap:16 }}>
          <DashCard title="Content Strategy Generator">
            <div style={{ fontSize:12, color:'var(--textMuted)', marginBottom:12, lineHeight:1.6 }}>
              Generate a data-driven content plan based on what travellers actually search for. The AI will scan travel blogs, forums, and search trends to find the content ideas most likely to drive organic traffic to {hotelName}.
            </div>
            <button onClick={runContent} disabled={contentLoading} style={{ ...btnPrimary, width:'auto', padding:'10px 28px', opacity:contentLoading?0.6:1 }}>
              {contentLoading ? '⏳ Researching content trends…' : '✍️ Generate Content Strategy'}
            </button>
          </DashCard>

          {contentLoading && <LoadingBar label="Scanning travel blogs, search trends and content gaps…" />}
          {contentError && <div style={{ background:'var(--bgDanger)', border:'1px solid #e74c3c44', borderRadius:8, padding:'12px 16px', color:'#e74c3c', fontSize:12 }}>⚠ {contentError}</div>}

          {contentResult && (
            <div style={{ display:'grid', gap:14 }}>
              {/* Blog posts */}
              {contentResult.blogPosts?.length > 0 && (
                <DashCard title="📝 Blog Post Ideas" titleColor={BLUE}>
                  <div style={{ display:'grid', gap:10 }}>
                    {contentResult.blogPosts.sort((a,b)=>(a.priority||5)-(b.priority||5)).map((post, i) => (
                      <div key={i} style={{ padding:'12px 14px', background:'var(--bgInput)', borderRadius:8 }}>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:8, marginBottom:6 }}>
                          <span style={{ fontSize:13, fontWeight:700, color:'var(--textPrimary)', flex:1 }}>{post.title}</span>
                          <div style={{ display:'flex', gap:5, flexShrink:0 }}>
                            <span style={{ fontSize:9, color: post.estimatedTraffic==='high'?GREEN:post.estimatedTraffic==='medium'?GOLD:'var(--textFaint)', fontWeight:700, background:(post.estimatedTraffic==='high'?GREEN:post.estimatedTraffic==='medium'?GOLD:'#888')+'18', padding:'2px 6px', borderRadius:10, textTransform:'uppercase' }}>{post.estimatedTraffic} traffic</span>
                          </div>
                        </div>
                        <div style={{ fontSize:10, color:ACCENT, fontWeight:600, marginBottom:6 }}>🎯 Target: {post.targetKeyword}</div>
                        {post.outline?.length > 0 && (
                          <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
                            {post.outline.map((h, j) => <span key={j} style={{ fontSize:10, color:'var(--textMuted)', background:'var(--bgCard)', border:'1px solid var(--border)', borderRadius:6, padding:'2px 8px' }}>{h}</span>)}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </DashCard>
              )}

              {/* Content calendar */}
              {contentResult.contentCalendar?.length > 0 && (
                <DashCard title="📅 Content Calendar" titleColor={PURPLE}>
                  <div style={{ overflowX:'auto' }}>
                    <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                      <thead>
                        <tr>{['Month','Theme','Key Content','SEO Focus'].map(h => (
                          <th key={h} style={{ padding:'8px 10px', textAlign:'left', fontSize:9, color:'var(--textFaint)', fontWeight:700, textTransform:'uppercase', letterSpacing:1, borderBottom:'2px solid var(--border)', whiteSpace:'nowrap' }}>{h}</th>
                        ))}</tr>
                      </thead>
                      <tbody>
                        {contentResult.contentCalendar.map((row, i) => (
                          <tr key={i} style={{ borderBottom:'1px solid var(--border)' }}>
                            <td style={{ padding:'9px 10px', color:GOLD, fontWeight:700 }}>{row.month}</td>
                            <td style={{ padding:'9px 10px', color:'var(--textSecond)', fontWeight:600 }}>{row.theme}</td>
                            <td style={{ padding:'9px 10px', color:'var(--textMuted)', fontSize:11 }}>{row.keyContent}</td>
                            <td style={{ padding:'9px 10px', color:ACCENT, fontSize:11 }}>{row.seoFocus}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </DashCard>
              )}

              {/* Social content */}
              {contentResult.socialContent?.length > 0 && (
                <DashCard title="📱 Social Media Content" titleColor={GREEN}>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))', gap:10 }}>
                    {contentResult.socialContent.map((s, i) => (
                      <div key={i} style={{ padding:'12px', background:'var(--bgInput)', borderRadius:8, display:'flex', flexDirection:'column', gap:6 }}>
                        <div style={{ display:'flex', justify:'space-between', alignItems:'center', gap:8 }}>
                          <span style={{ fontSize:12, fontWeight:700, color:'var(--textPrimary)' }}>{s.platform}</span>
                          {s.bestTime && <span style={{ fontSize:9, color:'var(--textFaint)' }}>⏰ {s.bestTime}</span>}
                        </div>
                        <div style={{ fontSize:11, color:'var(--textSecond)', lineHeight:1.5 }}>{s.idea}</div>
                        {s.hashtags?.length > 0 && (
                          <div style={{ display:'flex', flexWrap:'wrap', gap:3 }}>
                            {s.hashtags.map((h,j) => <span key={j} style={{ fontSize:9, color:BLUE, fontWeight:600 }}>{h}</span>)}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </DashCard>
              )}

              {/* Local content */}
              {contentResult.localContent?.length > 0 && (
                <DashCard title="📍 Local Content Opportunities" titleColor={GOLD}>
                  <div style={{ display:'grid', gap:8 }}>
                    {contentResult.localContent.map((item, i) => (
                      <div key={i} style={{ padding:'10px 14px', background:'var(--bgInput)', borderRadius:8 }}>
                        <div style={{ fontSize:12, fontWeight:700, color:'var(--textPrimary)', marginBottom:3 }}>{item.idea}</div>
                        <div style={{ display:'flex', gap:10 }}>
                          {item.keyword && <span style={{ fontSize:10, color:ACCENT }}>🎯 {item.keyword}</span>}
                          {item.why && <span style={{ fontSize:10, color:'var(--textFaint)' }}>{item.why}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </DashCard>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
